import {
	PrepareTransferInResponse,
	prepareTransferIn,
	TransferInParams,
	TransferInResponse,
} from "@/api/transactions";
import { paymentCredentialsOptions } from "@/features/payment-credentials/useGetPaymentCredentials";
import {
	completePendingTransferIn,
	isRetryableCompleteError,
} from "@/features/transactions/pendingTransferIn";
import { queryClient } from "@/query-client";
import { useEffect, useRef, useState } from "react";
import { SafeParseReturnType } from "zod";

export type IframeTransferInStatus =
	| "idle"
	| "preparing"
	| "charging"
	| "completing"
	| "success"
	| "error"
	// The card may have been charged but the outcome couldn't be confirmed (no answer from the
	// iframe or from transfer_in/complete). The charge is settled later (next app load or the
	// API cron), so the guest must not be told it failed nor retry it.
	| "unconfirmed";

type IframePayment = {
	accessToken: string;
	payment: PrepareTransferInResponse["payment"];
};

type Options = {
	guestId: number;
	cardId: string;
	locationId: number;
	onSettled?: () => void;
};

// process_card responses (approved and declined) carry the gateway transaction uuid.
function getTransactionId(response: unknown): string | null {
	const transactionId = (response as { transaction_id?: unknown } | null)
		?.transaction_id;
	return typeof transactionId === "string" && transactionId.length > 0
		? transactionId
		: null;
}

/**
 * Add Funds through the SoftPoint payment iframe with Visa DCAP:
 * 1. transfer_in/prepare: every check + the amounts, and a signed transfer intent.
 * 2. The iframe charges the card (TransferInPaymentIframe, rendered while status is "charging").
 * 3. transfer_in/complete: settles the transaction (fee split, payment, invoice, emails), or
 *    returns the risk level when the card was declined. Retried until it answers, and kept in
 *    storage meanwhile so a reload finishes it (see pendingTransferIn).
 */
export default function useIframeTransferIn({
	guestId,
	cardId,
	locationId,
	onSettled,
}: Options) {
	const [status, setStatus] = useState<IframeTransferInStatus>("idle");
	const [iframe, setIframe] = useState<IframePayment | null>(null);
	const [data, setData] = useState<SafeParseReturnType<
		unknown,
		TransferInResponse
	> | null>(null);
	const [error, setError] = useState<unknown>(null);
	const prepared = useRef<PrepareTransferInResponse | null>(null);
	const startedRef = useRef(false);

	// Leaving while the card is being charged or the charge is being settled would lose the
	// outcome: ask first.
	useEffect(() => {
		if (status !== "charging" && status !== "completing") return;
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [status]);

	const settle = (
		nextStatus: "success" | "error" | "unconfirmed",
		reason?: unknown,
	) => {
		setError(reason ?? null);
		setStatus(nextStatus);
		onSettled?.();
	};

	const start = async (params: TransferInParams) => {
		// One transfer per review: a double click must not prepare (and charge) twice.
		if (startedRef.current) return;
		startedRef.current = true;
		setStatus("preparing");
		setError(null);
		try {
			const [prepareResponse, credentials] = await Promise.all([
				prepareTransferIn(params),
				queryClient.fetchQuery(paymentCredentialsOptions(params.locationId)),
			]);
			prepared.current = prepareResponse;
			setIframe({
				accessToken: credentials.iframe_access_token,
				payment: prepareResponse.payment,
			});
			setStatus("charging");
		} catch (reason) {
			// Nothing was charged: the guest can try again.
			startedRef.current = false;
			settle("error", reason);
		}
	};

	const complete = async (transactionId: string) => {
		const intent = prepared.current;
		if (!intent) {
			settle("unconfirmed");
			return;
		}
		setStatus("completing");
		try {
			const result = await completePendingTransferIn({
				guestId,
				cardId,
				locationId,
				transferIntent: intent.transfer_intent,
				transactionId,
				expiresAt: intent.expires_at,
			});
			setData(result);
			settle("success");
		} catch (reason) {
			// A decline comes back here with details.risk_level, like transfer_in.
			settle(
				isRetryableCompleteError(reason) ? "unconfirmed" : "error",
				reason,
			);
		}
	};

	const onIframeApproved = (response: unknown) => {
		const transactionId = getTransactionId(response);
		if (!transactionId) {
			// Approved but without the transaction to settle: the API cron settles it.
			settle("unconfirmed", response);
			return;
		}
		complete(transactionId);
	};

	const onIframeDeclined = (response: unknown) => {
		const transactionId = getTransactionId(response);
		if (transactionId) {
			// A processor decline still created a transaction: complete it for the
			// risk level and the decline email, same as transfer_in.
			complete(transactionId);
			return;
		}
		settle("error", response);
	};

	// No answer from the iframe: the charge may or may not have gone through.
	const onIframeTimeout = () => settle("unconfirmed");

	return {
		status,
		iframe,
		data,
		error,
		start,
		onIframeApproved,
		onIframeDeclined,
		onIframeTimeout,
	};
}
